import { BadgeCheck, ClipboardList, Filter, UserCheck, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import {
  CANDIDATES_FILTER_PRESERVE_PARAM_KEYS,
  METRICS_CONFIG,
  PAGE_CONFIG,
  getCandidatesUiComponents,
} from "./CandidatesPageConfig";
import { useCandidatesPage } from "./useCandidatesPage";

import ModernPageLayout from "@/components/layout/ModernPageLayout";
import { Button } from "@/components/ui/v2/button";
import { FiltersDrawer } from "@/components/ui/v2/filters/FiltersDrawer";
import { TableActiveFilters } from "@/components/ui/v2/table/table-active-filters";
import UnavailablePage from "@/components/ui/v2/unavailable-page";
import MetricCard from "@/features/campaigns/jobboards/components/MetricCard";
import { useModuleStatus } from "@/features/integrations/api/useModuleStatus";
import {
  useCandidateFilterOptions,
  useCandidatesFilteredCount,
} from "@/features/integrations/modules/candidates/api";
import ReportLevelTabs from "@/features/integrations/shared/components/ReportLevelTabs";
import { renderComponent } from "@/features/integrations/shared/renderer";
import { ModuleName } from "@/features/integrations/utils/module-name";
import { useValueFormatter } from "@/utils/useValueFormatter";

const METRIC_ICON_CLASS = "h-4 w-4 lg:h-6 lg:w-6 text-primary";

export default function CandidatesPage() {
  const { t } = useTranslation();
  const { format: formatValue } = useValueFormatter();
  const { clientId } = useParams<{ clientId?: string }>();
  // Candidates is a level of the Applies report, so it needs applies — plus candidate-tracking, which
  // is toggled separately and is what actually populates candidates (OR-2155). Checked here as well as
  // on the tab, since the route is reachable by direct link.
  const applies = useModuleStatus(ModuleName.Applies, { clientId });
  const candidateTracking = useModuleStatus(ModuleName.CandidateTracking, { clientId });
  const modulesLoading = applies.isLoading || candidateTracking.isLoading;
  const modulesEnabled = !!applies.data?.enabled && !!candidateTracking.data?.enabled;
  const {
    context,
    clientScoped,
    filtersOpen,
    setFiltersOpen,
    filterSections,
    activeFilters,
    removeFilter,
    clearFilters,
    filtersLoading,
    totalRecords,
    countBaseParams,
    filterKeys,
    filterOptionParams,
    metricsLoading,
    totalCandidates,
    totalApplies,
    averageAppliesPerCandidate,
    qualifiedCount,
    qualifiedPercentage,
    hireableCount,
    hireablePercentage,
    searchParams,
  } = useCandidatesPage({ clientId });

  // Thin wrapper so the drawer can invoke the count hook with its pending filter selection.
  const useFilteredCount = (pending: Record<string, string[]>) =>
    useCandidatesFilteredCount(countBaseParams, filterKeys, pending);

  // Same binding for the drawer's paged dimensions: the drawer calls this once per async section with
  // that section's key, its debounced search box, and whether it is expanded.
  const useAsyncFilterOptions = (dimension: string, search: string, enabled: boolean) =>
    useCandidateFilterOptions(dimension, filterOptionParams, search, enabled);

  if (!modulesLoading && !modulesEnabled) {
    return <UnavailablePage variant="integrationDisabled" />;
  }

  const ui = getCandidatesUiComponents(t, clientScoped);

  // A null ratio means the backend had no candidates to divide by; the shared formatter renders the
  // same "N/A" the column renderer resolves showNullAs to, so cards and tables stay in step.
  const averageDisplay = formatValue(averageAppliesPerCandidate, "decimal");
  const qualifiedDisplay = formatValue(qualifiedPercentage, "percentage");
  const hireableDisplay = formatValue(hireablePercentage, "percentage");

  // Shows what the average divides, so the ratio can be read back to the applies behind it.
  const averageSubtitleCount =
    averageAppliesPerCandidate === null
      ? undefined
      : { count: totalApplies, labelKey: "integrations.cockpit.candidates.fromApplies" };

  // "0 of 0" adds nothing next to an N/A value, so drop the subtitle with it.
  const countOfTotal = (count: number) =>
    ({ count, total: totalCandidates, labelKey: "common.countOfTotal" });
  const qualifiedSubtitleCount =
    qualifiedPercentage === null ? undefined : countOfTotal(qualifiedCount);
  const hireableSubtitleCount =
    hireablePercentage === null ? undefined : countOfTotal(hireableCount);

  const filtersAction = (
    <Button variant="outline" size="sm" type="button" onClick={() => setFiltersOpen(true)}>
      <Filter className="h-4 w-4" />
      {t("common.filters", "Filters")}
    </Button>
  );

  return (
    <ModernPageLayout title={t(PAGE_CONFIG.titleKey)} subtitle={t(PAGE_CONFIG.subtitleKey)}>
      <div className="space-y-4">
        <ReportLevelTabs active="candidates" />

        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
          <MetricCard
            icon={<Users className={METRIC_ICON_CLASS} />}
            label={t(METRICS_CONFIG.totalCandidates.titleKey)}
            value={formatValue(totalCandidates, "whole")}
            variant="gradient"
            isLoading={metricsLoading}
          />
          <MetricCard
            icon={<ClipboardList className={METRIC_ICON_CLASS} />}
            label={t(METRICS_CONFIG.averageApplies.titleKey)}
            value={averageDisplay}
            subtitleCount={averageSubtitleCount}
            variant="gradient"
            isLoading={metricsLoading}
          />
          <MetricCard
            icon={<BadgeCheck className={METRIC_ICON_CLASS} />}
            label={t(METRICS_CONFIG.qualified.titleKey)}
            value={qualifiedDisplay}
            subtitleCount={qualifiedSubtitleCount}
            variant="gradient"
            isLoading={metricsLoading}
          />
          <MetricCard
            icon={<UserCheck className={METRIC_ICON_CLASS} />}
            label={t(METRICS_CONFIG.hireable.titleKey)}
            value={hireableDisplay}
            subtitleCount={hireableSubtitleCount}
            variant="gradient"
            isLoading={metricsLoading}
          />
        </div>

        {ui.map((component, index) => (
          <div key={`${component.component}-${index}`} className="space-y-4">
            {renderComponent(
              component,
              component.component === "tableToolbar"
                ? { ...context, customToolbarActions: filtersAction }
                : context,
            )}
            {component.component === "tableToolbar" && activeFilters.length > 0 && (
              <TableActiveFilters
                filters={activeFilters}
                onRemoveFilter={removeFilter}
                onClearFilters={clearFilters}
              />
            )}
          </div>
        ))}
      </div>

      <FiltersDrawer
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        searchParams={searchParams}
        filterKeys={[...filterKeys]}
        filterSections={filterSections}
        entityName={t("common.candidates", "Candidates")}
        filtersLoading={filtersLoading}
        totalCount={totalRecords}
        useFilteredCount={useFilteredCount}
        useAsyncFilterOptions={useAsyncFilterOptions}
        preserveParamKeys={[...CANDIDATES_FILTER_PRESERVE_PARAM_KEYS]}
        searchTag={context.searchValue ?? undefined}
      />
    </ModernPageLayout>
  );
}
